import type { Column } from '@talos/kyoko';
import { SmartTruncateEnum } from '@talos/kyoko';
import { useMemo } from 'react';
import { nullsLastNumericComparator } from '../FundingRateBlotter/fundingRateBlotterSort';
import type { AssetLeaderboardEntity } from './AssetLeaderboardEntity';
import {
  AssetLeaderboardChangeCell,
  AssetLeaderboardPercentCell,
  AssetLeaderboardValueCell,
} from './assetLeaderboardCells';
import { ASSET_LEADERBOARD_PRICE_CHANGE_7D_COLUMN_ID } from './tokens';

const USD_COLUMN_MIN_WIDTH = 120;
const PRICE_COLUMN_MIN_WIDTH = 140;

const USD_CELL_PARAMS = {
  headerClass: 'ag-right-aligned-header',
  cellClass: 'ag-right-aligned-cell',
  cellRenderer: AssetLeaderboardValueCell,
  comparator: nullsLastNumericComparator,
  cellRendererParams: { currency: 'USD' },
} as const;

const USD_COMPACT_CELL_PARAMS = {
  ...USD_CELL_PARAMS,
  cellRendererParams: { currency: 'USD', smartTruncate: SmartTruncateEnum.Compact, smartTruncateThreshold: 1000 },
} as const;

const PERCENT_CELL_PARAMS = {
  headerClass: 'ag-right-aligned-header',
  cellClass: 'ag-right-aligned-cell',
  cellRenderer: AssetLeaderboardPercentCell,
  comparator: nullsLastNumericComparator,
} as const;

/**
 * Asset Leaderboard columns. Market Cap defaults sorted descending via `initialSort` (not the live `sort`
 * field) so it only seeds the very first, never-persisted load — a returning user's persisted sort,
 * including one set by a leaderboard preset, isn't fought on every column-def re-render.
 */
export function useAssetLeaderboardColumns(): Column<unknown, AssetLeaderboardEntity>[] {
  return useMemo(
    () => [
      { type: 'currency', field: 'symbol', title: 'Asset', width: 180 },
      {
        type: 'custom',
        field: 'price',
        title: 'Price',
        flex: 1,
        minWidth: PRICE_COLUMN_MIN_WIDTH,
        // Price is the only live-ticking column, so it alone gets the green/red tick-direction icon.
        params: { ...USD_CELL_PARAMS, cellRendererParams: { currency: 'USD', showTrend: true } },
      },
      {
        type: 'custom',
        field: 'change1hPct',
        title: '1h %',
        width: 90,
        params: PERCENT_CELL_PARAMS,
      },
      {
        type: 'custom',
        field: 'change24hPct',
        title: '24h %',
        width: 90,
        params: PERCENT_CELL_PARAMS,
      },
      {
        type: 'custom',
        field: 'change7dPct',
        title: '7d %',
        width: 90,
        params: PERCENT_CELL_PARAMS,
      },
      {
        type: 'custom',
        field: 'marketCapUsd',
        title: 'Market Cap',
        flex: 1,
        minWidth: USD_COLUMN_MIN_WIDTH,
        initialSort: '-',
        params: USD_COMPACT_CELL_PARAMS,
      },
      {
        type: 'custom',
        field: 'spotVolumeUsd',
        title: 'Volume',
        flex: 1,
        minWidth: USD_COLUMN_MIN_WIDTH,
        params: USD_COMPACT_CELL_PARAMS,
      },
      {
        // Shares the `change7dPct` field with the plain 7d % column above, so it needs an explicit `id`
        // to get its own AG Grid colId (see ASSET_LEADERBOARD_PRICE_CHANGE_7D_COLUMN_ID).
        type: 'custom',
        id: ASSET_LEADERBOARD_PRICE_CHANGE_7D_COLUMN_ID,
        field: 'change7dPct',
        title: '7d Price Change',
        width: 110,
        params: {
          headerClass: 'ag-center-aligned-cell',
          cellClass: 'ag-center-aligned-cell',
          cellRenderer: AssetLeaderboardChangeCell,
          comparator: nullsLastNumericComparator,
        },
      },
    ],
    []
  );
}
